<template>
  <div class="attract-card">
    <div class="attract-card__head">
      <div class="attract-card__title">{{ $t('customerAttractionMethod') }}</div>
    </div>
    <div
      v-if="canSee"
      class="attract-card__body"
    >
      <div
        v-if="legendRows.length"
        class="attract-card__legend"
      >
        <div
          v-for="row in legendRows"
          :key="row.label"
          class="attract-card__legend-item"
        >
          <span
            class="attract-card__swatch"
            :style="{ background: row.color }"
          />
          <span class="attract-card__legend-name">{{ row.label }}</span>
          <span class="attract-card__legend-pct">{{ row.percent }}٪</span>
        </div>
      </div>
      <div
        v-if="legendRows.length"
        class="attract-card__chart"
      >
        <apexchart
          type="pie"
          height="180"
          :options="chartOptions"
          :series="series"
        />
      </div>
      <div
        v-else-if="!loading"
        class="attract-card__empty"
      >
        {{ $t('there is no enough information for showing.') }}
      </div>
      <v-progress-circular
        v-if="loading"
        :width="3"
        color="blue"
        indeterminate
        size="22"
      />
    </div>
    <div
      v-else
      class="attract-card__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
const PALETTE = ['#0066FF', '#4CAF50', '#FF5252', '#00CAE3', '#7C4DFF', '#FF9800']

export default {
  data () {
    return {
      items: [],
      series: [],
      colors: [],
      loading: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    total () {
      return this.series.reduce((sum, value) => sum + (Number(value) || 0), 0)
    },
    legendRows () {
      return this.items.map((item, index) => {
        const value = Number(item.value) || 0
        const percent = this.total ? Math.round((value / this.total) * 1000) / 10 : 0
        return {
          label: item.key,
          color: this.colors[index] || PALETTE[index % PALETTE.length],
          percent
        }
      })
    },
    chartOptions () {
      return {
        chart: {
          type: 'pie',
          fontFamily: 'IRANSans',
          toolbar: { show: false },
          animations: { enabled: false },
          parentHeightOffset: 0
        },
        labels: this.legendRows.map(row => row.label),
        colors: this.colors,
        legend: { show: false },
        stroke: {
          width: 2,
          colors: ['#fff']
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: 700,
            colors: ['#fff']
          },
          dropShadow: {
            enabled: true,
            color: '#111',
            top: 0,
            left: 0,
            blur: 2,
            opacity: 0.25
          },
          formatter (val) {
            if (val < 6) return ''
            return (Math.round(val * 10) / 10) + '٪'
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            customScale: 1,
            dataLabels: {
              offset: 0,
              minAngleToShowLabel: 18
            }
          }
        },
        tooltip: {
          y: {
            formatter (value) {
              return value
            }
          }
        }
      }
    }
  },
  mounted () {
    this.fetchMethods()
  },
  methods: {
    fetchMethods () {
      this.loading = true
      this.$axios.get(this.$endpoint + 'widgets', {
        params: { type: 'customerAttraction' }
      }).then(r => {
        this.items = r.data || []
        this.series = this.items.map(item => Number(item.value) || 0)
        this.colors = this.items.map((_, i) => PALETTE[i % PALETTE.length])
      }).catch(() => {
        this.items = []
        this.series = []
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.attract-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  height: 262px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.attract-card__head {
  flex-shrink: 0;
  margin-bottom: 4px;
}
.attract-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.attract-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.attract-card__legend {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}
.attract-card__legend-item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  white-space: nowrap;
}
.attract-card__swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}
.attract-card__legend-name {
  font-size: 12px;
  color: #374151;
  white-space: nowrap;
  flex-shrink: 0;
}
.attract-card__legend-pct {
  font-size: 11px;
  color: #9CA3AF;
  white-space: nowrap;
  flex-shrink: 0;
}
.attract-card__chart {
  flex: 1 1 auto;
  min-width: 0;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.attract-card__empty {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
/* Match HomeDebtorsCard height on tablet/mobile */
@media (max-width: 1220px) {
  .attract-card {
    height: 299px;
  }
}
@media (max-width: 767px) {
  .attract-card {
    height: 299px;
  }
  .attract-card__body {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 12px;
  }
  .attract-card__legend {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .attract-card__chart {
    width: 100%;
    height: 160px;
  }
}
</style>
